// TeamsProvider — owns the teams suite's preview state.
//
// TWO queries, fired in sequence rather than one merged round trip:
//
//   1. `filter=public` — the catalogue. Auth-independent (the route is public),
//      so it fires immediately and never waits on the auth handshake. This is
//      the query the landing hero renders from, and nothing else gates it.
//   2. `filter=owned`  — the viewer's saved teams. Fires only once the public
//      query has SETTLED, and only for a signed-in viewer.
//
// Why sequential: the hero is the first paint of the landing page and it shows
// the catalogue only. Racing a second, auth-gated request against it buys
// nothing for that paint and costs it contention. The owned teams are needed
// one surface later — the "all teams" gallery — so they are fetched after the
// thing the viewer is actually looking at has arrived.
//
// The owned query waits for public to SETTLE, not to succeed: a catalogue
// failure must not also strand the viewer's own teams, which are served by a
// different query and can still render.

'use client';

import { TeamsListFilter } from '@cabal/shared/domain/session/team/enums.js';
import type { TeamPreviewListItem } from '@cabal/shared/domain/session/team/types.js';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useApi } from '@/contexts/api';
import { useAuth } from '@/contexts/auth';
import { useQueryHook } from '@/lib/query/use-query-hook';
import { QUERY_STATUS, type QueryStatus } from '@/types/async-protocol';
import { teamsPreviewKey } from './team-query-keys';

export const TEAM_DATA_EXTENT = {
  Preview: 'preview',
  FullData: 'fullData',
} as const;
export type TeamDataExtent = typeof TEAM_DATA_EXTENT[keyof typeof TEAM_DATA_EXTENT];

export const TEAM_LIST_CATEGORY = {
  All: 'all',
  Ops: 'ops',
  Cortex: 'cortex',
  Vitals: 'vitals',
  Arcade: 'arcade',
} as const;
export type TeamListCategory = typeof TEAM_LIST_CATEGORY[keyof typeof TEAM_LIST_CATEGORY];

const EMPTY_TEAMS: TeamPreviewListItem[] = [];

interface TeamsContextValue {
  /** Public catalogue + the viewer's owned teams, deduped by id. */
  teams: TeamPreviewListItem[];
  /** The public catalogue, exactly as the `filter=public` query returned it. */
  publicTeams: TeamPreviewListItem[];
  /** The viewer's own saved, private teams. Empty when signed out. */
  ownedTeams: TeamPreviewListItem[];
  /** Fast lookup by id, over the merged list. */
  teamById: Record<string, TeamPreviewListItem>;
  /**
   * The CATALOGUE query's status — `publicTeams` and anything derived from it.
   * Never reflects the owned query: a surface that shows owned teams reads
   * `ownedStatus` instead, so a slow second round trip cannot hold back the
   * catalogue that already arrived.
   */
  status: QueryStatus;
  /**
   * The owned query's status. `Success` with an empty list for a signed-out
   * viewer — there is nothing to fetch, so that is a resolved answer and not a
   * pending one. `Loading` while auth is still resolving, or while the owned
   * query is queued behind the public one.
   */
  ownedStatus: QueryStatus;
  error: unknown | null;
  refetch: () => void;
}

const TeamsContext = createContext<TeamsContextValue | null>(null);

export function TeamsProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const auth = useAuth();
  const authLoading = auth.status === QUERY_STATUS.Loading;
  const userId = auth.data?.user.id ?? null;
  const isAuthed = userId !== null;

  const publicResult = useQueryHook<TeamPreviewListItem[]>({
    queryKey: teamsPreviewKey(TeamsListFilter.Public),
    queryFn: async () => {
      const response = await api.teams.listPreview({
        query: { filter: TeamsListFilter.Public },
      });
      return response?.teams ?? [];
    },
  });

  const isPublicSettled = publicResult.status !== QUERY_STATUS.Loading;
  const isOwnedEnabled = !authLoading && isAuthed && isPublicSettled;

  const ownedResult = useQueryHook<TeamPreviewListItem[]>({
    queryKey: teamsPreviewKey(TeamsListFilter.Owned),
    queryFn: async () => {
      const response = await api.teams.listPreview({
        query: { filter: TeamsListFilter.Owned },
      });
      return response?.teams ?? [];
    },
    options: { enabled: isOwnedEnabled },
  });

  const publicData = publicResult.data;
  const ownedData = ownedResult.data;
  const publicStatus = publicResult.status;
  const publicError = publicResult.error;
  const ownedError = ownedResult.error;
  const refetchPublic = publicResult.refetch;
  const refetchOwned = ownedResult.refetch;

  const ownedStatus = resolveOwnedStatus({
    authLoading,
    isAuthed,
    isPublicSettled,
    ownedQueryStatus: ownedResult.status,
  });

  const refetch = useCallback(() => {
    refetchPublic();
    refetchOwned();
  }, [refetchPublic, refetchOwned]);

  const value = useMemo<TeamsContextValue>(() => {
    const publicTeams = publicData ?? EMPTY_TEAMS;
    // `filter=owned` is scoped to the caller server-side, so ownership is
    // already guaranteed. The predicate that remains is a COLLECTION one: a
    // team the viewer owns AND has published belongs to the catalogue slice,
    // and listing it in both would double it in every merged view.
    const ownedTeams = isAuthed && ownedData !== null
      ? ownedData.filter((team) => team.isSaved && !team.isPublic)
      : EMPTY_TEAMS;

    const teamById: Record<string, TeamPreviewListItem> = {};
    for (const team of publicTeams) teamById[team.id] = team;
    for (const team of ownedTeams) teamById[team.id] = team;
    const teams = Object.values(teamById);

    return {
      teams,
      publicTeams,
      ownedTeams,
      teamById,
      status: publicStatus,
      ownedStatus,
      error: publicError ?? ownedError,
      refetch,
    };
  }, [
    publicData,
    ownedData,
    isAuthed,
    publicStatus,
    ownedStatus,
    publicError,
    ownedError,
    refetch,
  ]);

  return <TeamsContext.Provider value={value}>{children}</TeamsContext.Provider>;
}

interface ResolveOwnedStatusInput {
  authLoading: boolean;
  isAuthed: boolean;
  isPublicSettled: boolean;
  ownedQueryStatus: QueryStatus;
}

/**
 * `useQueryHook` reports a DISABLED query as `Loading` — it cannot tell "queued
 * behind its gate" from "in flight". Both of this hook's gates are therefore
 * resolved here rather than read off the query: auth unresolved and public
 * still in flight are genuinely pending, but a signed-out viewer is a finished
 * answer whose result is the empty list.
 */
function resolveOwnedStatus({
  authLoading,
  isAuthed,
  isPublicSettled,
  ownedQueryStatus,
}: ResolveOwnedStatusInput): QueryStatus {
  if (authLoading) return QUERY_STATUS.Loading;
  if (!isAuthed) return QUERY_STATUS.Success;
  if (!isPublicSettled) return QUERY_STATUS.Loading;
  return ownedQueryStatus;
}

export function useTeamsContext(): TeamsContextValue {
  const ctx = useContext(TeamsContext);
  if (!ctx) throw new Error('TeamsProvider not mounted');
  return ctx;
}
